import { __decorate } from "tslib";
import { ArrayDecoder, AutoEncoder, BooleanDecoder, DateDecoder, EnumDecoder, field, IntegerDecoder, StringDecoder } from '@simonbackx/simple-encoding';
import { SimpleError } from '@simonbackx/simple-errors';
import { v4 as uuidv4 } from 'uuid';
import { Country } from '../addresses/CountryDecoder.js';
import { upgradePriceFrom2To4DecimalPlaces } from '../upgradePriceFrom2To4DecimalPlaces.js';
export var STPackageType;
(function (STPackageType) {
    // Members without activities (not available in frontend anymore)
    STPackageType["LegacyMembers"] = "LegacyMembers";
    // Full members package
    STPackageType["Members"] = "Members";
    // Webshop package (max 10 webshops)
    STPackageType["Webshops"] = "Webshops";
    // One webshop package (max 1 webshop)
    STPackageType["SingleWebshop"] = "SingleWebshop";
    STPackageType["TrialMembers"] = "TrialMembers";
    STPackageType["TrialWebshops"] = "TrialWebshops";
})(STPackageType || (STPackageType = {}));
export class STPackageTypeHelper {
    static getName(type) {
        switch (type) {
            case STPackageType.LegacyMembers: return $t(`%oF`);
            case STPackageType.Members: {
                return $t(`%Om`);
            }
            case STPackageType.Webshops: {
                return $t(`%1Hk`);
            }
            case STPackageType.SingleWebshop: {
                return $t(`%oG`);
            }
            case STPackageType.TrialMembers: return $t(`%1Hl`);
            case STPackageType.TrialWebshops: return $t(`%1Hm`);
        }
    }
}
export var STPricingType;
(function (STPricingType) {
    STPricingType["Fixed"] = "Fixed";
    /**
     * Package is renewable per year
     * Package can get extended initially to a specific date
     */
    STPricingType["PerYear"] = "PerYear";
    /**
     * Price is per member, per year
     */
    STPricingType["PerMember"] = "PerMember";
})(STPricingType || (STPricingType = {}));
export class STPackageMeta extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.pricingType = STPricingType.Fixed;
        /**
         * One time price for the package, per year, or per member depending on pricingType
         */
        this.unitPrice = 0;
        /**
         * Service fees, percentage. 100_00 = 100%, 1 = 0.01%
         */
        this.serviceFeePercentage = 0;
        /**
         * Fixed service fee per payment, in cents
         */
        this.serviceFeeFixed = 0;
        /**
         * Fixed service fee per payment, in cents
         */
        this.serviceFeeMinimum = null;
        /**
         * Fixed service fee per payment, in cents
         */
        this.serviceFeeMaximum = null;
        /**
         * @deprecated
         * Contains the (paid) invoiced amount.
         *
         * This should be replaced by manually querying the balance items created for the package
         */
        this.paidAmount = 0;
        /**
         * @deprecated
         * Contains the (paid) invoiced price. Used for statistics and reporting
         * This should be replaced by manually querying the balance items created for the package
         */
        this.paidPrice = 0;
        /**
         * Minimum amount that will needs to get invoiced. Only used for first invoice
         */
        this.minimumAmount = 1;
        /**
         * Only used if applicable
         */
        this.autorenew = true;
        /**
         * Only used if applicable
         */
        this.allowRenew = false;
        /// If a top up is done, and the payment fails, set the date here
        /// + increase the payment failed count.
        /// After x days this will disable all functions if it keeps failing
        this.firstFailedPayment = null;
        this.paymentFailedCount = 0;
        this.canDeactivate = false;
    }
    get name() {
        return STPackageTypeHelper.getName(this.type);
    }
}
__decorate([
    field({ decoder: StringDecoder, optional: true })
], STPackageMeta.prototype, "didRenewId", void 0);
__decorate([
    field({ decoder: new EnumDecoder(STPackageType) })
], STPackageMeta.prototype, "type", void 0);
__decorate([
    field({ decoder: new EnumDecoder(STPricingType) })
], STPackageMeta.prototype, "pricingType", void 0);
__decorate([
    field({ decoder: IntegerDecoder }),
    field({ ...upgradePriceFrom2To4DecimalPlaces })
], STPackageMeta.prototype, "unitPrice", void 0);
__decorate([
    field({ decoder: IntegerDecoder, optional: true })
], STPackageMeta.prototype, "serviceFeePercentage", void 0);
__decorate([
    field({ decoder: IntegerDecoder, optional: true }),
    field({ ...upgradePriceFrom2To4DecimalPlaces, optional: true })
], STPackageMeta.prototype, "serviceFeeFixed", void 0);
__decorate([
    field({ decoder: IntegerDecoder, optional: true, nullable: true }),
    field({ ...upgradePriceFrom2To4DecimalPlaces, optional: true, nullable: true })
], STPackageMeta.prototype, "serviceFeeMinimum", void 0);
__decorate([
    field({ decoder: IntegerDecoder, optional: true, nullable: true }),
    field({ ...upgradePriceFrom2To4DecimalPlaces, optional: true, nullable: true })
], STPackageMeta.prototype, "serviceFeeMaximum", void 0);
__decorate([
    field({ decoder: IntegerDecoder })
], STPackageMeta.prototype, "paidAmount", void 0);
__decorate([
    field({ decoder: IntegerDecoder }),
    field({ ...upgradePriceFrom2To4DecimalPlaces })
], STPackageMeta.prototype, "paidPrice", void 0);
__decorate([
    field({ decoder: IntegerDecoder })
], STPackageMeta.prototype, "minimumAmount", void 0);
__decorate([
    field({ decoder: BooleanDecoder })
], STPackageMeta.prototype, "autorenew", void 0);
__decorate([
    field({ decoder: BooleanDecoder })
], STPackageMeta.prototype, "allowRenew", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true })
], STPackageMeta.prototype, "firstFailedPayment", void 0);
__decorate([
    field({ decoder: IntegerDecoder })
], STPackageMeta.prototype, "paymentFailedCount", void 0);
__decorate([
    field({ decoder: DateDecoder })
], STPackageMeta.prototype, "startDate", void 0);
__decorate([
    field({ decoder: BooleanDecoder })
], STPackageMeta.prototype, "canDeactivate", void 0);
/**
 * A package contains a price agreement for a given period that might be renewable
 */
export class STPackage extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.createdAt = new Date();
        this.updatedAt = new Date();
        /**
         * validAt is null if the initial needed payment is missing. The package can be ignore
         * It contains the date when the package was validated and added correctly.
         */
        this.validAt = null;
        this.validUntil = null;
        /// Disable / delete this package after this date (also no renew allowed). Can't keep using the currently saved pricing
        this.removeAt = null;
    }
    shouldHintRenew() {
        if (!this.meta.allowRenew || this.validUntil === null) {
            return false;
        }
        if (this.meta.startDate > new Date()) {
            // Not yet activated
            return false;
        }
        // Allow renew 2 months in advance
        const allowAfter = new Date(this.validUntil);
        allowAfter.setDate(allowAfter.getDate() - 31 * 2);
        if (allowAfter < new Date()) {
            return true;
        }
        return false;
    }
    get status() {
        return STPackageStatus.create({
            startDate: this.meta.startDate,
            validUntil: this.validUntil,
            removeAt: this.removeAt,
            firstFailedPayment: this.meta.firstFailedPayment,
        });
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], STPackage.prototype, "id", void 0);
__decorate([
    field({ decoder: STPackageMeta })
], STPackage.prototype, "meta", void 0);
__decorate([
    field({ decoder: DateDecoder })
], STPackage.prototype, "createdAt", void 0);
__decorate([
    field({ decoder: DateDecoder })
], STPackage.prototype, "updatedAt", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true })
], STPackage.prototype, "validAt", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true })
], STPackage.prototype, "validUntil", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true })
], STPackage.prototype, "removeAt", void 0);
export class STPackageStatusServiceFee extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.fixed = 0;
        this.percentage = 0;
        this.minimum = null;
        this.maximum = null;
        this.endDate = null;
    }
}
__decorate([
    field({ decoder: IntegerDecoder }),
    field({ ...upgradePriceFrom2To4DecimalPlaces })
], STPackageStatusServiceFee.prototype, "fixed", void 0);
__decorate([
    field({ decoder: IntegerDecoder })
], STPackageStatusServiceFee.prototype, "percentage", void 0);
__decorate([
    field({ decoder: IntegerDecoder, nullable: true, optional: true }),
    field({ ...upgradePriceFrom2To4DecimalPlaces, nullable: true, optional: true })
], STPackageStatusServiceFee.prototype, "minimum", void 0);
__decorate([
    field({ decoder: IntegerDecoder, nullable: true, optional: true }),
    field({ ...upgradePriceFrom2To4DecimalPlaces, nullable: true, optional: true })
], STPackageStatusServiceFee.prototype, "maximum", void 0);
__decorate([
    field({ decoder: DateDecoder })
], STPackageStatusServiceFee.prototype, "startDate", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true })
], STPackageStatusServiceFee.prototype, "endDate", void 0);
export class STPackageStatus extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.validUntil = null;
        /// Disable / delete this package after this date (also no renew allowed). Can't keep using the currently saved pricing
        this.removeAt = null;
        this.firstFailedPayment = null;
        /**
         * Service fees, percentage. 100_00 = 100%, 1 = 0.01%
         */
        this.serviceFees = [];
    }
    get activeServiceFees() {
        const now = new Date();
        const filtered = this.serviceFees.filter((fee) => {
            if (fee.startDate <= now && (fee.endDate === null || fee.endDate >= now)) {
                return true;
            }
            return false;
        });
        // Return maximum fees
        if (filtered.length === 0) {
            return STPackageStatusServiceFee.create({
                fixed: 0,
                percentage: 0,
                startDate: new Date(),
                endDate: null,
            });
        }
        if (filtered.length === 1) {
            return filtered[0]; // Only one fee, return it
        }
        console.warn('Multiple active service fees found, merging them', filtered);
        return filtered.reduce((max, current) => {
            var _a, _b;
            return STPackageStatusServiceFee.create({
                fixed: Math.max(max.fixed, current.fixed),
                percentage: Math.max(max.percentage, current.percentage),
                startDate: max.startDate,
                endDate: max.endDate,
                minimum: max.minimum !== null ? Math.max(max.minimum, (_a = current.minimum) !== null && _a !== void 0 ? _a : 0) : current.minimum,
                maximum: max.maximum !== null ? Math.max(max.maximum, (_b = current.maximum) !== null && _b !== void 0 ? _b : 0) : current.maximum,
            });
        });
    }
    get isActive() {
        const d = new Date();
        /// Active if it starts within 10 seconds (fixes time differences between server and clients)
        if (this.startDate && this.startDate > new Date(d.getTime() + 10 * 1000)) {
            return false;
        }
        if (this.removeAt && this.removeAt < d) {
            return false;
        }
        if (this.validUntil && this.validUntil < d) {
            return false;
        }
        // Deactivate module if payment failed, and not reactivated after 4 weeks
        const expire = new Date();
        expire.setDate(expire.getDate() - 28);
        if (this.firstFailedPayment && this.firstFailedPayment < expire) {
            return false;
        }
        return true;
    }
    /**
     * Purchased the package, the package is not yet removed, but it is expired or not paid after 4 weeks following a failed payment
     */
    get wasActive() {
        if (this.isActive) {
            return false;
        }
        const d = new Date();
        /// Active if it starts within 10 seconds (fixes time differences between server and clients)
        if (this.startDate && this.startDate > new Date(d.getTime() + 10 * 1000)) {
            return false;
        }
        // If only valid for less than 30 days, still allow to use the package
        if (this.validUntil && this.validUntil.getTime() - this.startDate.getTime() < 60 * 1000 * 60 * 24 * 31) {
            return false;
        }
        if (this.validUntil && this.validUntil < d) {
            // Passed!
            return true;
        }
        // Deactivate module if payment failed, and not reactivated after 4 weeks
        const expire = new Date();
        expire.setDate(expire.getDate() - 28);
        if (this.firstFailedPayment && this.firstFailedPayment < expire) {
            // did not pay!
            return true;
        }
        return false;
    }
    get deactivateDate() {
        const dates = [];
        if (this.removeAt !== null) {
            dates.push(this.removeAt);
        }
        if (this.validUntil !== null) {
            dates.push(this.validUntil);
        }
        if (this.firstFailedPayment !== null) {
            const expire = new Date(this.firstFailedPayment);
            expire.setDate(expire.getDate() + 28);
            dates.push(expire);
        }
        if (dates.length === 0) {
            return null;
        }
        return new Date(Math.min(...dates.map(d => d.getTime())));
    }
    merge(status) {
        if (status.startDate < this.startDate) {
            this.startDate = status.startDate;
            // TODO: fix behaviour with gaps if we allow that in the future
        }
        if (status.validUntil === null) {
            this.validUntil = null;
        }
        else if (this.validUntil !== null) {
            if (status.validUntil > this.validUntil) {
                this.validUntil = status.validUntil;
            }
        }
        if (status.removeAt === null) {
            this.removeAt = null;
        }
        else if (this.removeAt !== null) {
            if (status.removeAt > this.removeAt) {
                this.removeAt = status.removeAt;
            }
        }
        if (this.firstFailedPayment === null) {
            this.firstFailedPayment = status.firstFailedPayment;
        }
        else if (status.firstFailedPayment !== null) {
            if (status.firstFailedPayment < this.firstFailedPayment) {
                this.firstFailedPayment = status.firstFailedPayment;
            }
        }
        this.serviceFees.push(...status.serviceFees);
    }
}
__decorate([
    field({ decoder: DateDecoder })
], STPackageStatus.prototype, "startDate", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true })
], STPackageStatus.prototype, "validUntil", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true })
], STPackageStatus.prototype, "removeAt", void 0);
__decorate([
    field({ decoder: DateDecoder, nullable: true })
], STPackageStatus.prototype, "firstFailedPayment", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(STPackageStatusServiceFee), optional: true })
], STPackageStatus.prototype, "serviceFees", void 0);
export function calculateVATPercentage(address, VATNumber) {
    // Determine VAT rate
    let VATRate = 0;
    if (address.country === Country.Belgium) {
        VATRate = 21;
    }
    else {
        if (VATNumber && VATNumber.substr(0, 2).toUpperCase() !== 'BE') {
            VATRate = 0;
        }
        else {
            // Apply VAT rate of the home country for consumers in the EU
            switch (address.country) {
                case Country.Netherlands:
                    VATRate = 21;
                    break;
                case Country.Luxembourg:
                    VATRate = 17;
                    break;
                case Country.France:
                    VATRate = 20;
                    break;
                case Country.Germany:
                    VATRate = 19;
                    break;
                default: {
                    throw new SimpleError({
                        code: 'country_not_supported',
                        message: 'Non-business sales to your country are not yet supported. Please enter a valid VAT number.',
                    });
                }
            }
        }
    }
    return VATRate;
}
//# sourceMappingURL=STPackage.js.map